import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    Grid,
    TextField,
    Typography,
    Autocomplete,
    IconButton,
    InputAdornment,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    MenuItem,
    Select,
    Chip,
    FormControl,
    InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { energizedWorkPermitService } from '../../../services/energizedWorkPermitService';
import { generateUUID } from '../../../utils/uuid';
import { STATUS_DRAFT, STATUS_SUBMITTED, JOB_STORE } from '../../../config/constants';
import { initializeDB } from '../../../config/db';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import axiosInstance from '../../../axiosInstance';
import useDictation from '../../../hooks/useDictation';

const EnergizedWorkPermitForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');
    const saveTimeoutRef = useRef(null);
    const sigPadRef = useRef(null);

    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        status: STATUS_DRAFT,
        
        // Job info
        jobCode: '',
        jobName: '',
        location: '',
        date: dayjs().format('YYYY-MM-DD'),
        
        // Part I - Requester
        requesterName: '',
        requesterTitle: '',
        requesterDate: dayjs().format('YYYY-MM-DD'),
        circuitDescription: '',
        workDescription: '',
        justification: '',
        
        // Part II - Electrically Qualified Persons (dynamic array)
        qualifiedPersons: [
            {
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            }
        ],
        
        jobProcedure: '',
        safeWorkPractices: '',
        shockHazardAnalysis: [],
        arcFlashHazardAnalysis: [],
        meansToRestrictAccess: '',
        jobBriefingEvidence: '',
        
        // Part III - Approvals (dynamic array)
        approvers: [
            {
                role: 'Manufacturing Manager',
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            },
            {
                role: 'Maintenance Manager',
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            },
            {
                role: 'Knowledgeable Person',
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            },
            {
                role: 'Project Manager',
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            },
            {
                role: 'Qualified Person Doing Work',
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            },
            {
                role: 'Safety Coordinator',
                name: '',
                date: dayjs().format('YYYY-MM-DD'),
                inputMode: 'text',
                signature: null
            }
        ],
        
        createdAt: null,
        updatedAt: null
    });

    const [errors, setErrors] = useState({});
    const [jobs_loading, setJobsLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    // Modal states for each person field
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [currentEmployeeField, setCurrentEmployeeField] = useState(null);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [currentSignatureField, setCurrentSignatureField] = useState(null);
    const [existingSignature, setExistingSignature] = useState(null);

    // Dictation hook
    const [activeDictationField, setActiveDictationField] = useState(null);
    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
        onError: (error) => {
            console.error('Dictation error:', error);
        }
    });

    const toggleDictation = async (fieldName) => {
        if (!dictationSupported) {
            console.error('Speech recognition not supported in this browser');
            return;
        }
        
        if (isListening && activeDictationField === fieldName) {
            stopDictation();
            setActiveDictationField(null);
        } else {
            if (isListening) {
                stopDictation();
            }
            setActiveDictationField(fieldName);
            await startDictation({
                onResult: (transcript) => {
                    if (!transcript) return;
                    setForm(currentForm => {
                        const currentText = currentForm[fieldName] || '';
                        const needsSpace = currentText && !currentText.endsWith(' ');
                        const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                        const updated = {
                            ...currentForm,
                            [fieldName]: newText
                        };
                        queueSave(updated);
                        return updated;
                    });
                },
                onError: (e) => {
                    console.error('Recognition error:', e?.error || e);
                }
            });
        }
    };

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const jobList = await db.getAll(JOB_STORE);
            setJobs(jobList || []);
        } catch (error) {
            console.error('Error loading jobs:', error);
        } finally {
            setJobsLoading(false);
        }
    };

    const loadFormData = async () => {
        if (!uuid) return;
        
        try {
            const data = await energizedWorkPermitService.getFromIndexedDB(uuid);
            if (data) {
                // Ensure qualifiedPersons is an array with at least 1 entry
                if (!Array.isArray(data.qualifiedPersons) || data.qualifiedPersons.length === 0) {
                    data.qualifiedPersons = [{
                        name: '',
                        date: dayjs().format('YYYY-MM-DD'),
                        inputMode: 'text',
                        signature: null
                    }];
                }
                setForm(data);
                setSubmitted(data.status === STATUS_SUBMITTED);
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    const loadedRef = useRef(false);
    
    useEffect(() => {
        if (loadedRef.current) {
            return;
        }
        
        loadJobs();
        if (uuid) {
            loadFormData();
        }
        loadedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    const queueSave = (data) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await energizedWorkPermitService.saveToIndexedDB(data);
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        }, 300);
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const handleJobChange = (event, newValue) => {
        if (newValue) {
            const updated = {
                ...form,
                jobCode: newValue.Job || newValue.JobCode,
                jobName: newValue.JobName,
                location: newValue.location || ''
            };
            setForm(updated);
            queueSave(updated);
        } else {
            const updated = { ...form, jobCode: '', jobName: '', location: '' };
            setForm(updated);
            queueSave(updated);
        }
    };

    const openEmployeeModal = (fieldName) => {
        setCurrentEmployeeField(fieldName);
        setEmployeeModalOpen(true);
    };

    const handleSelectEmployee = (selected) => {
        if (selected && selected.length > 0 && currentEmployeeField) {
            const employee = selected[0];
            
            // Check if it's a qualified person field
            if (currentEmployeeField.startsWith('qualifiedPerson') && !isNaN(currentEmployeeField.replace('qualifiedPerson', ''))) {
                const index = parseInt(currentEmployeeField.replace('qualifiedPerson', ''));
                updateQualifiedPerson(index, 'name', employee.FullName);
            } 
            // Check if it's an approver field
            else if (currentEmployeeField.startsWith('approver') && !isNaN(currentEmployeeField.replace('approver', ''))) {
                const index = parseInt(currentEmployeeField.replace('approver', ''));
                updateApprover(index, 'name', employee.FullName);
            } 
            else {
                const updated = { ...form, [currentEmployeeField]: employee.FullName };
                setForm(updated);
                queueSave(updated);
            }
        }
        setEmployeeModalOpen(false);
        setCurrentEmployeeField(null);
    };

    const addQualifiedPerson = () => {
        const currentPersons = Array.isArray(form.qualifiedPersons) ? form.qualifiedPersons : [];
        const updated = {
            ...form,
            qualifiedPersons: [
                ...currentPersons,
                {
                    name: '',
                    date: dayjs().format('YYYY-MM-DD'),
                    inputMode: 'text',
                    signature: null
                }
            ]
        };
        setForm(updated);
        queueSave(updated);
    };

    const removeQualifiedPerson = (index) => {
        const currentPersons = Array.isArray(form.qualifiedPersons) ? form.qualifiedPersons : [];
        if (currentPersons.length > 1) {
            const updated = {
                ...form,
                qualifiedPersons: currentPersons.filter((_, i) => i !== index)
            };
            setForm(updated);
            queueSave(updated);
        }
    };

    const updateQualifiedPerson = (index, field, value) => {
        const currentPersons = Array.isArray(form.qualifiedPersons) ? form.qualifiedPersons : [];
        const updated = {
            ...form,
            qualifiedPersons: currentPersons.map((person, i) =>
                i === index ? { ...person, [field]: value } : person
            )
        };
        setForm(updated);
        queueSave(updated);
    };

    const updateApprover = (index, field, value) => {
        const currentApprovers = Array.isArray(form.approvers) ? form.approvers : [];
        const updated = {
            ...form,
            approvers: currentApprovers.map((approver, i) =>
                i === index ? { ...approver, [field]: value } : approver
            )
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleInputModeChange = (field, mode) => {
        const updated = { ...form, [`${field}InputMode`]: mode };
        setForm(updated);
        queueSave(updated);
    };

    const handleSubmit = async () => {
        // Validation
        const newErrors = {};
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.requesterTitle) newErrors.requesterTitle = 'Requester/Title is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire('Validation Error', 'Please fill in all required fields', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Form?',
            text: 'Are you sure you want to submit this Energized Electrical Work Permit?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Submit',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            const dataToSubmit = {
                ...form,
                status: STATUS_SUBMITTED
            };

            // Save to IndexedDB first
            await energizedWorkPermitService.saveToIndexedDB(dataToSubmit);

            // Submit to API
            const response = await axiosInstance.post('/energized-work-permit/submit', dataToSubmit);

            if (response.data.success) {
                await Swal.fire('Success', 'Energized Electrical Work Permit submitted successfully', 'success');
                navigate('/electrical/energized-work-permit');
            } else {
                throw new Error(response.data.message || 'Submission failed');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire('Error', error.response?.data?.message || 'Failed to submit form', 'error');
        } finally {
            setSaving(false);
        }
    };

    const sortedJobs = React.useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            Job: job.Job || job.JobCode,
            JobCode: job.JobCode || job.Job,
            label: `${job.Job || job.JobCode} - ${job.JobName}`
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    const selectedJob = sortedJobs.find(j => (j.Job === form.jobCode || j.JobCode === form.jobCode));

    const renderPersonField = (fieldName, label, dateField) => {
        const inputMode = form[`${fieldName}InputMode`] || 'text';
        const value = form[fieldName] || '';
        const dateValue = form[dateField] || '';

        return (
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={label}
                                value={value}
                                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                disabled={inputMode === 'select'}
                                InputProps={{
                                    endAdornment: inputMode === 'select' ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => openEmployeeModal(fieldName)}
                                                edge="end"
                                            >
                                                <PersonIcon fontSize="small" />
                                            </IconButton>
                                            {value && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleFieldChange(fieldName, '')}
                                                    edge="end"
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ) : null
                                }}
                                onClick={() => {
                                    if (inputMode === 'select') {
                                        openEmployeeModal(fieldName);
                                    }
                                }}
                                sx={{
                                    cursor: inputMode === 'select' ? 'pointer' : 'text'
                                }}
                            />
                        </Box>
                        <ToggleButtonGroup
                            value={inputMode}
                            exclusive
                            onChange={(e, newMode) => {
                                if (newMode !== null) {
                                    handleInputModeChange(fieldName, newMode);
                                }
                            }}
                            size="small"
                            sx={{ height: 40 }}
                        >
                            <ToggleButton value="text" title="Type manually">
                                <EditIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="select" title="Select from list">
                                <PersonIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Date"
                        value={dateValue}
                        onChange={(e) => handleFieldChange(dateField, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/electrical/energized-work-permit')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>
                        Energized Electrical Work Permit
                    </Typography>
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
                {/* Job Information */}
                <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                        Job Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                options={sortedJobs}
                                getOptionLabel={(option) => option.label || ''}
                                value={selectedJob || null}
                                onChange={handleJobChange}
                                loading={jobs_loading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Job *"
                                        size="small"
                                        error={!!errors.jobCode}
                                        helperText={errors.jobCode}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => 
                                    (option.Job === value.Job || option.JobCode === value.JobCode)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Date"
                                value={form.date || ''}
                                onChange={(e) => handleFieldChange('date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Requester/Title *"
                                value={form.requesterTitle || ''}
                                onChange={(e) => handleFieldChange('requesterTitle', e.target.value)}
                                error={!!errors.requesterTitle}
                                helperText={errors.requesterTitle}
                                InputProps={{
                                    readOnly: true,
                                    style: { cursor: 'pointer' },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <PersonIcon />
                                        </InputAdornment>
                                    )
                                }}
                                onClick={() => openEmployeeModal('requesterTitle')}
                            />
                        </Grid>
                    </Grid>
                </Card>

                {/* Part I - Requester */}
                <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                        PART I: TO BE COMPLETED BY THE REQUESTER
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(1) Description of circuit/equipment/job location"
                                value={form.circuitDescription || ''}
                                onChange={(e) => handleFieldChange('circuitDescription', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('circuitDescription')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'circuitDescription' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'circuitDescription' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(2) Description of work to be done"
                                value={form.workDescription || ''}
                                onChange={(e) => handleFieldChange('workDescription', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('workDescription')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'workDescription' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'workDescription' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(3) Justification of why the circuit/equipment cannot be de-energized or the work deferred until the next scheduled outage"
                                value={form.justification || ''}
                                onChange={(e) => handleFieldChange('justification', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('justification')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'justification' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'justification' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>
                </Card>

                {/* Part II - Electrically Qualified Persons */}
                <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                        PART II: TO BE COMPLETED BY THE ELECTRICALLY QUALIFIED PERSONS DOING THE WORK
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(1) Detailed job description procedure to be used in performing the above detailed work"
                                value={form.jobProcedure || ''}
                                onChange={(e) => handleFieldChange('jobProcedure', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('jobProcedure')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'jobProcedure' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'jobProcedure' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(2) Description of the safe work practices to be employed"
                                value={form.safeWorkPractices || ''}
                                onChange={(e) => handleFieldChange('safeWorkPractices', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('safeWorkPractices')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'safeWorkPractices' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'safeWorkPractices' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="(3) Results of the shock hazard analysis"
                                SelectProps={{
                                    multiple: true,
                                    value: Array.isArray(form.shockHazardAnalysis) ? form.shockHazardAnalysis : [],
                                    onChange: (e) => handleFieldChange('shockHazardAnalysis', e.target.value),
                                    renderValue: (selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )
                                }}
                            >
                                <MenuItem value="(a) Limited approach boundary">(a) Limited approach boundary</MenuItem>
                                <MenuItem value="(b) Restricted approach boundary">(b) Restricted approach boundary</MenuItem>
                                <MenuItem value="(c) Prohibited approach boundary">(c) Prohibited approach boundary</MenuItem>
                                <MenuItem value="(d) Necessary shock PPE">(d) Necessary shock PPE</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="(4) Results of the arc flash hazard analysis"
                                SelectProps={{
                                    multiple: true,
                                    value: Array.isArray(form.arcFlashHazardAnalysis) ? form.arcFlashHazardAnalysis : [],
                                    onChange: (e) => handleFieldChange('arcFlashHazardAnalysis', e.target.value),
                                    renderValue: (selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )
                                }}
                            >
                                <MenuItem value="(a) Available incident energy">(a) Available incident energy</MenuItem>
                                <MenuItem value="(b) Necessary arc flash PPE">(b) Necessary arc flash PPE</MenuItem>
                                <MenuItem value="(c) Arc flash boundary">(c) Arc flash boundary</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(5) Means employed to restrict the access of unqualified persons from the work area"
                                value={form.meansToRestrictAccess || ''}
                                onChange={(e) => handleFieldChange('meansToRestrictAccess', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('meansToRestrictAccess')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'meansToRestrictAccess' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'meansToRestrictAccess' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                size="small"
                                label="(6) Evidence of completion of a job briefing, including discussion of any job-related hazards"
                                value={form.jobBriefingEvidence || ''}
                                onChange={(e) => handleFieldChange('jobBriefingEvidence', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => toggleDictation('jobBriefingEvidence')}
                                                edge="end"
                                                color={isListening && activeDictationField === 'jobBriefingEvidence' ? 'error' : 'default'}
                                            >
                                                {isListening && activeDictationField === 'jobBriefingEvidence' ? <StopIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
                                (7) Do you agree the above described work can be done safely?
                            </Typography>
                        </Grid>

                        {form.qualifiedPersons && form.qualifiedPersons.map((person, index) => (
                            <Grid item xs={12} key={index}>
                                <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Electrically Qualified Person #{index + 1}:
                                        </Typography>
                                        {form.qualifiedPersons.length > 1 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeQualifiedPerson(index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Name</Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={person.inputMode}
                                            exclusive
                                            onChange={(e, newMode) => {
                                                if (newMode !== null) {
                                                    const currentPersons = Array.isArray(form.qualifiedPersons) ? form.qualifiedPersons : [];
                                                    const updated = {
                                                        ...form,
                                                        qualifiedPersons: currentPersons.map((p, i) =>
                                                            i === index ? { ...p, inputMode: newMode, name: '' } : p
                                                        )
                                                    };
                                                    setForm(updated);
                                                    queueSave(updated);
                                                }
                                            }}
                                            size="small"
                                        >
                                            <ToggleButton value="text" sx={{ px: 2 }}>TEXT</ToggleButton>
                                            <ToggleButton value="select" sx={{ px: 2 }}>EMP</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Grid container spacing={2} alignItems="flex-start">
                                        <Grid item xs={12} sm={5}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={person.inputMode === 'select' ? 'Select Employee' : 'Enter Name'}
                                                value={person.name || ''}
                                                onChange={(e) => updateQualifiedPerson(index, 'name', e.target.value)}
                                                InputProps={{
                                                    readOnly: person.inputMode === 'select',
                                                    style: { cursor: person.inputMode === 'select' ? 'pointer' : 'text' },
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {person.inputMode === 'select' ? <PersonIcon /> : <EditIcon />}
                                                        </InputAdornment>
                                                    )
                                                }}
                                                onClick={() => {
                                                    if (person.inputMode === 'select') {
                                                        openEmployeeModal(`qualifiedPerson${index}`);
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="date"
                                                label="Date"
                                                value={person.date || ''}
                                                onChange={(e) => updateQualifiedPerson(index, 'date', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                onClick={(e) => {
                                                    if (e.target.showPicker) {
                                                        e.target.showPicker();
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {person.signature ? (
                                                    <>
                                                        <Box
                                                            component="img"
                                                            src={person.signature}
                                                            alt="Signature"
                                                            sx={{
                                                                maxWidth: 150,
                                                                maxHeight: 40,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 1,
                                                                cursor: 'pointer',
                                                                flexGrow: 1
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentSignatureField(`qualifiedPersonSig${index}`);
                                                                setExistingSignature(person.signature);
                                                                setSignatureModalOpen(true);
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                updateQualifiedPerson(index, 'signature', null);
                                                            }}
                                                        >
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<EditIcon />}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setCurrentSignatureField(`qualifiedPersonSig${index}`);
                                                            setExistingSignature(null);
                                                            setSignatureModalOpen(true);
                                                        }}
                                                        fullWidth
                                                    >
                                                        Sign
                                                    </Button>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addQualifiedPerson}
                                fullWidth
                            >
                                Add Electrically Qualified Person
                            </Button>
                        </Grid>
                    </Grid>
                </Card>

                {/* Part III - Approvals */}
                <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                        PART III: APPROVAL(S) TO PERFORM THE WORK WHILE ELECTRICALLY ENERGIZED
                    </Typography>
                    <Grid container spacing={2}>
                        {form.approvers && form.approvers.map((approver, index) => (
                            <Grid item xs={12} key={index}>
                                <Card variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {approver.role}:
                                    </Typography>
                                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Name</Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={approver.inputMode}
                                            exclusive
                                            onChange={(e, newMode) => {
                                                if (newMode !== null) {
                                                    const currentApprovers = Array.isArray(form.approvers) ? form.approvers : [];
                                                    const updated = {
                                                        ...form,
                                                        approvers: currentApprovers.map((a, i) =>
                                                            i === index ? { ...a, inputMode: newMode, name: '' } : a
                                                        )
                                                    };
                                                    setForm(updated);
                                                    queueSave(updated);
                                                }
                                            }}
                                            size="small"
                                        >
                                            <ToggleButton value="text" sx={{ px: 2 }}>TEXT</ToggleButton>
                                            <ToggleButton value="select" sx={{ px: 2 }}>EMP</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Grid container spacing={2} alignItems="flex-start">
                                        <Grid item xs={12} sm={5}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={approver.inputMode === 'select' ? 'Select Employee' : 'Enter Name'}
                                                value={approver.name || ''}
                                                onChange={(e) => updateApprover(index, 'name', e.target.value)}
                                                InputProps={{
                                                    readOnly: approver.inputMode === 'select',
                                                    style: { cursor: approver.inputMode === 'select' ? 'pointer' : 'text' },
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {approver.inputMode === 'select' ? <PersonIcon /> : <EditIcon />}
                                                        </InputAdornment>
                                                    )
                                                }}
                                                onClick={() => {
                                                    if (approver.inputMode === 'select') {
                                                        openEmployeeModal(`approver${index}`);
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="date"
                                                label="Date"
                                                value={approver.date || ''}
                                                onChange={(e) => updateApprover(index, 'date', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                onClick={(e) => {
                                                    if (e.target.showPicker) {
                                                        e.target.showPicker();
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {approver.signature ? (
                                                    <>
                                                        <Box
                                                            component="img"
                                                            src={approver.signature}
                                                            alt="Signature"
                                                            sx={{
                                                                maxWidth: 150,
                                                                maxHeight: 40,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 1,
                                                                cursor: 'pointer',
                                                                flexGrow: 1
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentSignatureField(`approverSig${index}`);
                                                                setExistingSignature(approver.signature);
                                                                setSignatureModalOpen(true);
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                updateApprover(index, 'signature', null);
                                                            }}
                                                        >
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<EditIcon />}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setCurrentSignatureField(`approverSig${index}`);
                                                            setExistingSignature(null);
                                                            setSignatureModalOpen(true);
                                                        }}
                                                        fullWidth
                                                    >
                                                        Sign
                                                    </Button>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Card>

                {/* Submit Button */}
                {!submitted && (
                    <Box display="flex" justifyContent="center" mt={2} mb={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                            sx={{ minWidth: 200 }}
                        >
                            {saving ? 'Submitting...' : 'Submit Permit'}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Employee Selection Modal */}
            <SelectEmployeesAndTempsModal
                open={employeeModalOpen}
                onClose={() => {
                    setEmployeeModalOpen(false);
                    setCurrentEmployeeField(null);
                }}
                onSelect={handleSelectEmployee}
                singleSelect={true}
                alreadySelected={currentEmployeeField && form[currentEmployeeField] ? [{ FullName: form[currentEmployeeField] }] : []}
            />

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                sigPadRef={sigPadRef}
                employeeName={
                    currentSignatureField && currentSignatureField.startsWith('qualifiedPersonSig')
                        ? form.qualifiedPersons?.[parseInt(currentSignatureField.replace('qualifiedPersonSig', ''))]?.name || ''
                        : currentSignatureField && currentSignatureField.startsWith('approverSig')
                        ? form.approvers?.[parseInt(currentSignatureField.replace('approverSig', ''))]?.name || ''
                        : ''
                }
                onClose={() => {
                    setSignatureModalOpen(false);
                    setCurrentSignatureField(null);
                    setExistingSignature(null);
                }}
                onAccept={(signatureData) => {
                    if (currentSignatureField && currentSignatureField.startsWith('qualifiedPersonSig')) {
                        const index = parseInt(currentSignatureField.replace('qualifiedPersonSig', ''));
                        const sigData = typeof signatureData === 'string' ? signatureData : sigPadRef.current?.toDataURL();
                        if (sigData) {
                            updateQualifiedPerson(index, 'signature', sigData);
                        }
                    } else if (currentSignatureField && currentSignatureField.startsWith('approverSig')) {
                        const index = parseInt(currentSignatureField.replace('approverSig', ''));
                        const sigData = typeof signatureData === 'string' ? signatureData : sigPadRef.current?.toDataURL();
                        if (sigData) {
                            updateApprover(index, 'signature', sigData);
                        }
                    }
                    setSignatureModalOpen(false);
                    setCurrentSignatureField(null);
                    setExistingSignature(null);
                }}
                existingSignature={existingSignature}
            />
        </Box>
    );
};

export default EnergizedWorkPermitForm;

